/**
 * Visualizador regional — Sprint 14.5 + rediseño post-feedback (mayo 19, 2026).
 *
 * Versión v1: treemap recharts. Problema descubierto en uso real: las
 * celdas chicas no caben nombres, y el muni focal del usuario (caso
 * Herveo en Tolima) podía quedar sin nombre. "Unos con nombre y otros
 * no" rompe la sensación de calidad.
 *
 * Versión v2 (mayo 19, 2026): ranking list horizontal con barras de
 * progreso. Decisión basada en recomendación del skill ui-ux-pro-max
 * (Bar Chart horizontal sorted DESC + value labels — accesible, todos
 * los nombres visibles, fácil de comparar).
 *
 * Versión v3 (Sprint 19.x, mayo 26, 2026): toggle Histórico ↔ Reciente.
 *
 * Versión v4 (Sprint Unificación GESPROY, mayo 27, 2026): eliminamos
 * el toggle. La cifra de captación del header, del score hero, del
 * heatmap y del drill-down ahora viene toda de la misma fuente
 * (`sgr_projects_historical` / GESPROY). Decisión del founder: "lo más
 * claro posible y verificable" — el toggle confundía porque las dos
 * opciones medían cosas distintas (recursos aprobados PIIP vs proyectos
 * con seguimiento físico GESPROY). Vamos con GESPROY, recursos
 * aprobados sin ejecución aún no se incluyen — eventualmente entran a
 * GESPROY cuando se inicia la ejecución.
 *
 * Mantenemos el nombre del componente y data-testid `department-heatmap`
 * para no romper integraciones / tests (FichaMunicipal lo monta así).
 *
 * Características:
 * - Card destacada arriba del ranking del muni focal del usuario.
 * - Lista scrolleable interna (max 480px alto) para no inflar la ficha.
 * - Animation fade-in + bar fill al cargar.
 * - Auto-scroll al muni focal al primer mount.
 * - Hover con highlight + tooltip nativo (no requiere recharts).
 * - Sin dependencia de SVG/recharts para esta sección — pura UI Tailwind.
 *
 * Telemetría `heatmap_viewed` se dispara desde el componente padre
 * (FichaMunicipal) — esto sigue siendo "el panel regional".
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";

import { track } from "@/lib/analytics";
import { ApiError, fetchDepartmentHeatmap, type HeatmapDTO, type HeatmapMuniDTO } from "@/lib/api";

interface DepartmentHeatmapProps {
  /** DANE del muni de la ficha (5 dígitos). Define el departamento y es
   * el origen de `heatmap_muni_clicked`. */
  dane: string | null | undefined;
  /** DANE del muni del usuario (el del tenant, no el de la URL: issue
   * #726). Resalta su fila y dispara auto-scroll dentro de la lista. Si
   * es null o de otro departamento, el heatmap se renderiza sin card
   * focal. */
  focalDane: string | null | undefined;
  /** Data inicial que ya cargó el padre. Sirve como `initialData` para
   * evitar un fetch redundante en el primer paint. */
  initialData?: HeatmapDTO;
}

interface RankedMuni {
  rank: number;
  dane_code: string;
  name: string;
  total_cop: string;
  total_cop_number: number;
  intensity: number;
  is_focal: boolean;
  project_count: number;
}

function formatCop(raw: string | number): string {
  const n = typeof raw === "string" ? Number(raw) : raw;
  if (!Number.isFinite(n) || n === 0) return "$0";
  if (n >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}MM`;
  if (n >= 1_000_000) return `$${Math.round(n / 1_000_000)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n.toLocaleString("es-CO")}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function DepartmentHeatmap({ dane, focalDane, initialData }: DepartmentHeatmapProps) {
  // Departamento inferido del DANE (2 primeros dígitos). Si el padre
  // no nos pasa dane, no podemos consultar — el padre ya guardó contra
  // ese caso (no renderiza el panel sin dane).
  const departmentCode = (dane ?? "").slice(0, 2);

  const heatmapQuery = useQuery({
    queryKey: ["dept-heatmap", departmentCode, focalDane],
    queryFn: () => fetchDepartmentHeatmap(departmentCode, focalDane),
    enabled: Boolean(dane) && departmentCode.length === 2,
    staleTime: 60 * 60 * 1000, // 1h, alineado con cache HTTP del BE
    initialData,
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
        return false;
      }
      return count < 2;
    },
  });

  const data = heatmapQuery.data;
  const isLoading = heatmapQuery.isLoading || heatmapQuery.isFetching;

  const focalRef = useRef<HTMLLIElement | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);
  // Guard para auto-scroll: queremos hacerlo SOLO la primera vez que
  // la lista renderiza con el focal. Si el usuario hace scroll manual
  // y luego cambia data (improbable acá, pero defensivo), no
  // queremos pegarle un jump.
  const autoScrolledRef = useRef(false);

  // Ranking: orden DESC por captación; rank empieza en 1; munis sin
  // captación (cop=0) van al final con rank pero opacity bajada.
  const ranked = useMemo<RankedMuni[]>(() => {
    if (!data) return [];
    return [...data.munis]
      .sort((a, b) => Number(b.total_cop) - Number(a.total_cop))
      .map((m: HeatmapMuniDTO, idx) => ({
        rank: idx + 1,
        dane_code: m.dane_code,
        name: m.name,
        total_cop: m.total_cop,
        total_cop_number: Number(m.total_cop),
        intensity: m.intensity,
        is_focal: m.is_focal,
        project_count: m.project_count,
      }));
  }, [data]);

  const focal = ranked.find((m) => m.is_focal);

  // Auto-scroll al muni focal cuando aparece — solo dentro del
  // contenedor (no scrolleamos toda la página). En jsdom (tests)
  // `scrollTo` no existe; guardamos defensivamente para no romper
  // los tests del componente.
  useEffect(() => {
    if (autoScrolledRef.current) return;
    if (!focalRef.current || !listRef.current) return;
    const container = listRef.current;
    const target = focalRef.current;
    if (typeof container.scrollTo !== "function") {
      autoScrolledRef.current = true;
      return;
    }
    const offset = target.offsetTop - container.offsetTop;
    const targetScroll = offset - container.clientHeight / 2 + target.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, targetScroll), behavior: "smooth" });
    autoScrolledRef.current = true;
  }, [focal]);

  if (isLoading && !data) {
    return (
      <div data-testid="department-heatmap" className="space-y-4">
        <HeatmapHeader departmentName="..." subtitle={null} />
        <div className="h-[300px] animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }

  if (heatmapQuery.isError || !data) {
    return (
      <div data-testid="department-heatmap" className="space-y-4">
        <HeatmapHeader departmentName="el departamento" subtitle={null} />
        <p className="text-sm text-slate-500">
          No se pudo cargar el mapa regional. Intenta recargar la página.
        </p>
      </div>
    );
  }

  // Empty state: depto sin actividad SGR. Mantener el data-testid
  // `heatmap-empty` para compatibilidad con tests existentes.
  // El test condicional "todos con total_cop=0" del SAMPLE: si TODOS
  // los munis tienen 0 captación, ranked queda con rank pero sin
  // barra → tratamos eso como empty para la sección.
  const someoneHasActivity = ranked.some((m) => m.total_cop_number > 0);
  // Issue #560: Bogotá D.C. (depto 11) tiene un solo municipio, así que
  // el conteo va en singular. Su nombre DIVIPOLA termina en ".", por lo
  // que no se agrega otro punto al cerrar la frase del empty state.
  const munisLabel = `${data.total_munis} ${data.total_munis === 1 ? "municipio" : "municipios"}`;
  const emptyEndsWithPeriod = data.department_name.endsWith(".");
  if (!someoneHasActivity) {
    return (
      <div data-testid="department-heatmap" className="space-y-4">
        <HeatmapHeader departmentName={data.department_name} subtitle={<>{munisLabel}</>} />
        <div
          className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center"
          data-testid="heatmap-empty"
        >
          <p className="text-sm text-slate-600">
            Sin actividad SGR registrada en {data.department_name}
            {emptyEndsWithPeriod ? "" : "."}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Cuando se inicien proyectos con seguimiento físico, aparecerán aquí.
          </p>
        </div>
        <GesproyDisclaimer />
      </div>
    );
  }

  return (
    <div data-testid="department-heatmap" className="space-y-4">
      <HeatmapHeader
        departmentName={data.department_name}
        subtitle={
          <>
            {munisLabel} · líder{" "}
            <span className="font-medium text-slate-700">{formatCop(data.max_total_cop)}</span> ·
            datos al {formatDate(data.data_as_of)}
          </>
        }
      />

      {/* Card destacada del muni focal — visible siempre, no requiere
          scroll. Comunica de un vistazo "dónde estás en la región". */}
      {focal && (
        <div
          data-testid="heatmap-focal-card"
          className="rounded-xl border-2 border-brand-500 bg-linear-to-br from-brand-50 to-white p-4 shadow-xs"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 text-brand-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77l-5.2 2.73.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
                </svg>
              </span>
              <div>
                <p className="text-xs font-semibold tracking-wide text-brand-700 uppercase">
                  Tu municipio
                </p>
                <p className="text-base font-bold text-slate-900">{focal.name}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Posición</p>
              <p className="text-lg font-bold text-brand-700 tabular-nums">
                #{focal.rank}
                <span className="ml-1 text-sm font-medium text-slate-500">de {ranked.length}</span>
              </p>
            </div>
          </div>
          <div className="mt-3 flex items-baseline justify-between gap-2">
            <span className="text-2xl font-extrabold text-slate-900 tabular-nums">
              {formatCop(focal.total_cop)}
            </span>
            <span className="text-xs text-slate-500">
              {focal.project_count} {focal.project_count === 1 ? "proyecto" : "proyectos"}
            </span>
          </div>
        </div>
      )}

      {/* Ranking list — scrollable, muestra TODOS los munis con nombre.
          La altura máxima evita que la sección crezca demasiado en
          deptos con muchos munis (Antioquia tiene 125). */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-4 py-2 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
          <span>Ranking del departamento</span>
          <span>Scroll para ver todos →</span>
        </div>
        <ol
          ref={listRef}
          className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto"
          aria-label={`Ranking SGR ${data.department_name}`}
        >
          {ranked.map((m) => (
            <RankRow
              key={m.dane_code}
              muni={m}
              sourceDane={dane ?? ""}
              setFocalRef={
                m.is_focal
                  ? (el) => {
                      focalRef.current = el;
                    }
                  : undefined
              }
            />
          ))}
        </ol>
      </div>

      <GesproyDisclaimer />
    </div>
  );
}

interface HeatmapHeaderProps {
  departmentName: string;
  /** Bloque ya-formateado para el subtítulo (línea de metadata).
   * Puede ser null durante loading/empty para suprimirlo. */
  subtitle: React.ReactNode | null;
}

/** Header simple con título + subtítulo. Separado del cuerpo principal
 * para reutilizarlo en los estados loading, error y empty sin duplicar
 * markup. */
function HeatmapHeader({ departmentName, subtitle }: HeatmapHeaderProps) {
  return (
    <header className="min-w-0">
      <h3 className="text-base font-semibold text-slate-900">Captación SGR en {departmentName}</h3>
      {subtitle !== null && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
    </header>
  );
}

/** Disclaimer fijo de la fuente única (GESPROY). Aclara que solo se
 * cuentan proyectos con seguimiento físico real — no recursos
 * aprobados sin ejecución — para que el alcalde pueda verificar las
 * cifras contra el portal MinHacienda. */
function GesproyDisclaimer() {
  return (
    <p className="text-[11px] text-slate-400">
      Datos de proyectos SGR en ejecución física (GESPROY). Recursos aprobados sin seguimiento aún
      no se incluyen. Fuente: MinHacienda.
    </p>
  );
}

interface RankRowProps {
  muni: RankedMuni;
  /** DANE del muni de la ficha (origen del view) — necesario para el evento
   * `heatmap_muni_clicked` que correlaciona origen → destino. */
  sourceDane: string;
  /** Callback que recibe el DOM node — usado para auto-scroll al
   * muni focal. undefined para munis no focales. */
  setFocalRef?: (el: HTMLLIElement | null) => void;
}

/** Una fila del ranking — animada y resaltada si es el muni focal.
 *
 * Sprint Pre-Demo: cada fila es ahora un `<Link>` a la ficha del muni
 * (`/municipio/:dane`), incluida la fila focal (loguea con `is_focal=true`
 * aunque la "navegación" sea a la misma página, dato de engagement útil).
 * Telemetría `heatmap_muni_clicked` se dispara onClick y no bloquea la
 * navegación. */
function RankRow({ muni, sourceDane, setFocalRef }: RankRowProps) {
  // Bar width: intensity (0-1) ya viene normalizada contra el max del
  // depto. Mínimo 2% para que un muni con captación muy chica aún
  // muestre algo (sin ese floor, la barra es invisible y solo se ve
  // el monto).
  const barWidth = muni.total_cop_number > 0 ? Math.max(2, muni.intensity * 100) : 0;
  // Color de la barra: gradiente más oscuro mientras más alta la
  // intensity. Brand para focal. Slate-200 para munis sin captación
  // (visible pero claramente "vacío").
  const barColorClass = muni.is_focal
    ? "bg-linear-to-r from-brand-400 to-brand-700"
    : muni.intensity >= 0.6
      ? "bg-linear-to-r from-blue-300 to-blue-700"
      : muni.intensity >= 0.25
        ? "bg-linear-to-r from-blue-200 to-blue-500"
        : muni.intensity > 0
          ? "bg-blue-200"
          : "bg-slate-100";

  const rowClass = muni.is_focal
    ? "bg-brand-50/60 ring-1 ring-inset ring-brand-200"
    : "hover:bg-slate-50";

  return (
    <li ref={setFocalRef} data-testid={muni.is_focal ? "rank-row-focal" : "rank-row"}>
      <Link
        to={`/municipio/${muni.dane_code}`}
        onClick={() => {
          track({
            event_name: "heatmap_muni_clicked",
            properties: {
              source_dane: sourceDane,
              clicked_dane: muni.dane_code,
              clicked_rank: muni.rank,
              clicked_name: muni.name,
              is_focal: muni.is_focal,
            },
          });
        }}
        className={`group relative flex items-center gap-3 px-4 py-2.5 transition-colors ${rowClass}`}
      >
        {/* Rank — tabular-nums para que # alineen verticalmente */}
        <span
          className={`w-9 shrink-0 text-sm font-semibold tabular-nums ${muni.is_focal ? "text-brand-700" : "text-slate-400"}`}
        >
          #{muni.rank}
        </span>

        {/* Name + bar — flex-1 para llenar el espacio */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className={`truncate text-sm ${muni.is_focal ? "font-bold text-brand-700" : "font-medium text-slate-700"}`}
              title={muni.name}
            >
              {muni.name}
              {muni.is_focal && (
                <span className="ml-2 rounded-full bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-brand-700 uppercase">
                  Tú
                </span>
              )}
            </span>
            <span
              className={`shrink-0 tabular-nums ${
                muni.is_focal
                  ? "text-sm font-bold text-brand-700"
                  : muni.total_cop_number > 0
                    ? "text-sm font-semibold text-slate-700"
                    : "text-xs text-slate-400"
              }`}
            >
              {formatCop(muni.total_cop)}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
            {/* La barra usa width con transition para "fill" animado al
                cargar. style inline porque tailwind no soporta width
                dinámico fluido (sin agregar safelist). */}
            <div
              className={`h-full rounded-full transition-[width] duration-700 ease-out ${barColorClass}`}
              style={{ width: `${barWidth}%` }}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Project count — solo visible en hover/focus, no satura la fila */}
        <span className="hidden shrink-0 text-[11px] text-slate-400 tabular-nums group-hover:inline-block sm:inline-block">
          {muni.project_count > 0 ? `${muni.project_count} proy.` : "—"}
        </span>
      </Link>
    </li>
  );
}
